import { ref, computed, inject } from 'vue'

// AI API Configuration
const AI_API_BASE_URL = 'https://ai-gateway.manonsanoi.workers.dev'
const DEFAULT_TTL_HOURS = 24 // 7 days
const DEFAULT_MAX_MESSAGES = 500

// AI Persona for Live Support
const AI_PERSONA = `You are a Thai Live Support Agent.

CRITICAL - Personal Information Collection:
- MUST collect: First Name, Last Name, Phone, Email
- Ask within first 2-3 messages
- Ask one piece at a time naturally
- When complete, summarize in Thai

Examples (respond in Thai):
- "ขอทราบชื่อของคุณครับ เพื่อให้เรียกได้ถูกต้อง"
- "ขอเบอร์โทรศัพท์ได้ไหมครับ"
- "ขออีเมล์ของคุณหน่อยครับ"
- "ขอทราบนามสกุลด้วยครับ"

Always respond in Thai, be helpful and polite.`

// Session management และ metadata storage
const sessionId = ref(null)
const conversationHistory = ref([])
const conversationMetadata = ref({}) // เพิ่มสำหรับเก็บ control_status และข้อมูล metadata อื่นๆ
const isLoadingHistory = ref(false)
const isLoadingResponse = ref(false)
const lastResponseMetadata = ref(null)

// Personal information tracking
const collectedPersonalInfo = ref({
  firstName: null,
  lastName: null,
  phone: null,
  email: null,
  isComplete: false,
  collectedAt: null
})

// Helper function to get hostname (moved inside useAIChat)

export const useAIChat = (hostname = 'localhost') => {
  // Get hostname from parameter instead of inject
  const getHostname = () => {
    console.log('[AI-API] Debug hostname:', { hostname })
    return hostname
  }
  // Generate session ID from timestamp and random token
  const generateSessionId = () => {
    const timestamp = Date.now()
    const randomToken = Math.random().toString(36).substring(2, 15)
    return `chat-${timestamp}-${randomToken}`
  }

  // Get existing session ID from cookie (ไม่สร้างใหม่)
  const getExistingSessionId = () => {
    if (sessionId.value) return sessionId.value

    if (process.client) {
      // Try to get from cookie
      const cookieValue = document.cookie
        .split('; ')
        .find(row => row.startsWith('ai-chat-session='))
        ?.split('=')[1]

      if (cookieValue) {
        sessionId.value = cookieValue
        return cookieValue
      }
    }

    return null // ไม่สร้างใหม่ ถ้าไม่มี
  }

  // Create new session ID (เฉพาะเมื่อต้องการ)
  const createSessionId = () => {
    if (sessionId.value) return sessionId.value

    // Generate new session ID
    sessionId.value = generateSessionId()
    
    if (process.client) {
      // Set cookie with 7 days expiration
      const expiryDate = new Date()
      expiryDate.setDate(expiryDate.getDate() + 7)
      document.cookie = `ai-chat-session=${sessionId.value}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`
    }

    return sessionId.value
  }

  // Get session ID from cookie or generate new one (legacy - เปลี่ยนให้ใช้ getExistingSessionId)
  const getSessionId = () => {
    return getExistingSessionId()
  }

  // Load conversation history
  const loadConversationHistory = async () => {
    const currentSessionId = getExistingSessionId() // ใช้ getExistingSessionId แทน
    console.log('[AI-API] 📚 loadConversationHistory called for session:', currentSessionId)
    
    if (!currentSessionId) {
      console.log('[AI-API] ❌ No session ID found - returning empty history')
      return [] // return empty array ถ้าไม่มี session
    }

    isLoadingHistory.value = true
    try {
      const hostname = getHostname()
      console.log('[AI-API] 🌐 Fetching history from API for session:', currentSessionId)
      
      const response = await fetch(
        `${AI_API_BASE_URL}/conversation/history?session_id=${currentSessionId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Hostname': hostname
          }
        }
      )

      if (response.ok) {
        const data = await response.json()
        // ปรับให้ตรงกับ format ใหม่
        conversationHistory.value = data.conversation || data.messages || []
        
        // เก็บ metadata รวมถึง control_status
        conversationMetadata.value = {
          control_status: data.control_status || null,
          session_info: data.session_info || null,
          last_updated: data.last_updated || null
        }
        
        console.log('[AI-API] ✅ Loaded conversation history:', conversationHistory.value.length, 'messages')
        console.log('[AI-API] 📊 Conversation metadata:', conversationMetadata.value)
        return conversationHistory.value
      } else {
        console.warn('[AI-API] ⚠️ Failed to load conversation history. Status:', response.status)
        return []
      }
    } catch (error) {
      console.error('[AI-API] ❌ Error loading conversation history:', error)
      return []
    } finally {
      isLoadingHistory.value = false
      console.log('[AI-API] 🏁 loadConversationHistory completed')
    }
  }

  // Send message to AI
  const sendMessageToAI = async (question, options = {}) => {
    // สร้าง session เฉพาะเมื่อจะส่งข้อความ
    const currentSessionId = createSessionId()
    if (!currentSessionId) {
      throw new Error('Unable to create session ID')
    }

    isLoadingResponse.value = true
    try {
      const hostname = getHostname()
      
      const response = await fetch(`${AI_API_BASE_URL}/bot/ask`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-ID': currentSessionId,
          'X-TTL-Hours': String(options.ttlHours || DEFAULT_TTL_HOURS),
          'X-Max-Messages': String(options.maxMessages || DEFAULT_MAX_MESSAGES),
          'X-Persona': 'support',
          'X-Hostname': hostname
        },
        body: JSON.stringify({
          question: question
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      
      // Handle response format with success flag
      if (data.success) {
        // Store metadata
        lastResponseMetadata.value = {
          responseTime: data.responseTime,
          sessionSettings: data.session_settings,
          timestamp: data.timestamp || new Date().toISOString(),
          sessionId: currentSessionId
        }
        
        // Check if answer is empty or null (admin mode)
        const answer = data.answer?.trim()
        if (!answer || answer === '') {
          console.log('[AI-API] Admin mode detected - no response to display')
          return null // Return null to indicate no bubble should be shown
        }
        
        return answer
      } else {
        throw new Error(data.error || 'Unknown error occurred')
      }
    } catch (error) {
      console.error('Error sending message to AI:', error)
      throw error
    } finally {
      isLoadingResponse.value = false
    }
  }

  // Initialize AI session (ไม่สร้าง session ทันที)
  const initializeAISession = async () => {
    const currentSessionId = getExistingSessionId() // ใช้ getExistingSessionId
    console.log('Checked existing AI chat session:', currentSessionId || 'No session yet')
    return currentSessionId
  }

  // Clear AI session
  const clearAISession = async () => {
    sessionId.value = null
    conversationHistory.value = []
    conversationMetadata.value = {}
    lastResponseMetadata.value = null
    
    if (process.client) {
      // Remove session cookie
      document.cookie = 'ai-chat-session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
    }
  }

  // Get conversation stats
  const getConversationStats = computed(() => {
    return {
      sessionId: sessionId.value,
      totalMessages: conversationHistory.value.length,
      isActive: !!sessionId.value,
      lastActivity: conversationHistory.value.length > 0 
        ? conversationHistory.value[conversationHistory.value.length - 1]?.timestamp 
        : null
    }
  })

  // Get last AI response metadata
  const getLastResponseMetadata = () => {
    return lastResponseMetadata.value || {
      responseTime: null,
      sessionSettings: null,
      timestamp: null,
      sessionId: null
    }
  }

  // Personal information management
  const updatePersonalInfo = (field, value) => {
    if (['firstName', 'lastName', 'phone', 'email'].includes(field)) {
      collectedPersonalInfo.value[field] = value
      
      // Check if all information is collected
      const { firstName, lastName, phone, email } = collectedPersonalInfo.value
      if (firstName && lastName && phone && email && !collectedPersonalInfo.value.isComplete) {
        collectedPersonalInfo.value.isComplete = true
        collectedPersonalInfo.value.collectedAt = new Date().toISOString()
        console.log('Personal information collection completed:', collectedPersonalInfo.value)
      }
    }
  }

  const getPersonalInfoStatus = () => {
    const { firstName, lastName, phone, email } = collectedPersonalInfo.value
    return {
      collected: {
        firstName: !!firstName,
        lastName: !!lastName,
        phone: !!phone,
        email: !!email
      },
      values: collectedPersonalInfo.value,
      isComplete: collectedPersonalInfo.value.isComplete,
      missing: [
        !firstName && 'First Name',
        !lastName && 'Last Name',
        !phone && 'Phone Number',
        !email && 'Email Address'
      ].filter(Boolean)
    }
  }

  const clearPersonalInfo = () => {
    collectedPersonalInfo.value = {
      firstName: null,
      lastName: null,
      phone: null,
      email: null,
      isComplete: false,
      collectedAt: null
    }
  }

  // Enhanced AI Persona with personal info context
  const getEnhancedPersona = () => {
    const infoStatus = getPersonalInfoStatus()
    let enhancedPersona = AI_PERSONA
    
    if (!infoStatus.isComplete && infoStatus.missing.length > 0) {
      enhancedPersona += `\n\nMissing: ${infoStatus.missing.join(', ')}
Ask for ONE missing piece now!`
    }
    
    if (infoStatus.isComplete) {
      enhancedPersona += `\n\nInfo Complete: ${infoStatus.values.firstName} ${infoStatus.values.lastName}, ${infoStatus.values.phone}, ${infoStatus.values.email}`
    }
    
    return enhancedPersona
  }

  // Initialize session on first use (ไม่สร้าง session ทันที)
  const initializeSession = async () => {
    const currentSessionId = getExistingSessionId() // ตรวจสอบ session ที่มีอยู่
    console.log('Checked existing AI chat session:', currentSessionId || 'No session yet')
    
    // Load existing conversation history เฉพาะถ้ามี session
    if (currentSessionId) {
      await loadConversationHistory()
    }
    
    return currentSessionId
  }

  return {
    // State
    sessionId: computed(() => sessionId.value),
    conversationHistory: computed(() => conversationHistory.value),
    conversationMetadata: computed(() => conversationMetadata.value),
    isLoadingHistory: computed(() => isLoadingHistory.value),
    isLoadingResponse: computed(() => isLoadingResponse.value),
    lastResponseMetadata: computed(() => lastResponseMetadata.value),
    personalInfo: computed(() => collectedPersonalInfo.value),
    
    // Methods
    sendMessageToAI,
    loadConversationHistory,
    initializeAISession,
    clearAISession,
    getLastResponseMetadata,
    initializeSession,
    createSessionId, // เพิ่มฟังก์ชันสำหรับสร้าง session เมื่อจำเป็น
    getExistingSessionId, // เพิ่มฟังก์ชันสำหรับตรวจสอบ session ที่มีอยู่
    
    // Personal Info Management
    updatePersonalInfo,
    getPersonalInfoStatus,
    clearPersonalInfo,
    
    // Computed
    getConversationStats
  }
}

// Helper function to extract personal information from messages
export const extractPersonalInfo = (message) => {
  const info = {}
  
  // Email pattern
  const emailPattern = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g
  const emails = message.match(emailPattern)
  if (emails && emails.length > 0) {
    info.email = emails[0]
  }
  
  // Phone pattern (Thai phone numbers)
  const phonePattern = /(?:0[6-9]\d{8}|66[6-9]\d{8}|\+66[6-9]\d{8})/g
  const phones = message.match(phonePattern)
  if (phones && phones.length > 0) {
    info.phone = phones[0]
  }
  
  // Thai name patterns (basic detection)
  const thaiNamePattern = /[\u0E01-\u0E5B\s]{2,30}/g
  const thaiNames = message.match(thaiNamePattern)
  if (thaiNames && thaiNames.length > 0) {
    const cleanName = thaiNames[0].trim()
    const nameParts = cleanName.split(/\s+/)
    if (nameParts.length >= 2) {
      info.firstName = nameParts[0]
      info.lastName = nameParts.slice(1).join(' ')
    } else if (nameParts.length === 1 && cleanName.length > 1) {
      // If only one part, treat as first name
      info.firstName = cleanName
    }
  }
  
  return info
}

// Helper function to format AI conversation history to chat messages
export const formatHistoryToMessages = (history) => {
  console.log('[AI-API] 🔄 formatHistoryToMessages called with:', history?.length || 0, 'items')
  
  if (!Array.isArray(history)) {
    console.log('[AI-API] ⚠️ History is not an array, returning empty array')
    return []
  }
  
  const messages = []
  const messageTexts = new Set() // Track unique message texts to prevent duplicates
  
  history.forEach((item, index) => {
    // รองรับ format ใหม่และเก่า
    const question = item.question || item.user_message || item.message
    const answer = item.answer || item.response || item.ai_response
    const timestamp = item.timestamp || item.created_at || Date.now()
    
    // รองรับ format แบบ role/content (Chat format)
    const role = item.role
    const content = item.content
    
    console.log('[AI-API] 📝 Processing history item', index, ':', {
      role,
      content: content?.substring(0, 50) + '...',
      question: question?.substring(0, 50) + '...',
      answer: answer?.substring(0, 50) + '...'
    })
    
    // Handle Chat format (role/content)
    if (role && content) {
      const messageType = role === 'user' ? 'user' : 'agent'
      const messageKey = `${messageType}:${content.trim()}`
      
      // Skip if we already have this exact message
      if (!messageTexts.has(messageKey)) {
        messageTexts.add(messageKey)
        messages.push({
          id: `history-${messageType}-${index}`,
          type: messageType,
          text: content,
          timestamp: new Date(timestamp),
          isHistory: true,
          isFromHistory: true,
          metadata: {
            sessionId: item.session_id || item.id,
            originalIndex: index,
            role: role
          }
        })
        console.log('[AI-API] ➕ Added message:', messageType, content.substring(0, 30) + '...')
      } else {
        console.log('[AI-API] ⏭️ Skipped duplicate message:', messageType, content.substring(0, 30) + '...')
      }
    } else {
      // Handle traditional Q&A format
      if (question) {
        const messageKey = `user:${question.trim()}`
        if (!messageTexts.has(messageKey)) {
          messageTexts.add(messageKey)
          messages.push({
            id: `history-user-${index}`,
            type: 'user',
            text: question,
            timestamp: new Date(timestamp),
            isHistory: true,
            isFromHistory: true,
            metadata: {
              sessionId: item.session_id,
              originalIndex: index
            }
          })
          console.log('[AI-API] ➕ Added user question:', question.substring(0, 30) + '...')
        } else {
          console.log('[AI-API] ⏭️ Skipped duplicate user message:', question.substring(0, 30) + '...')
        }
      }
      
      if (answer) {
        const messageKey = `agent:${answer.trim()}`
        if (!messageTexts.has(messageKey)) {
          messageTexts.add(messageKey)
          messages.push({
            id: `history-agent-${index}`,
            type: 'agent',
            text: answer,
            timestamp: new Date(timestamp),
            isHistory: true,
            isFromHistory: true,
            metadata: {
              sessionId: item.session_id,
              responseTime: item.responseTime,
              originalIndex: index
            }
          })
          console.log('[AI-API] ➕ Added agent answer:', answer.substring(0, 30) + '...')
        } else {
          console.log('[AI-API] ⏭️ Skipped duplicate agent message:', answer.substring(0, 30) + '...')
        }
      }
    }
  })
  
  console.log('[AI-API] ✅ formatHistoryToMessages completed. Final count:', messages.length, 'messages')
  return messages
}
